/**
 * DocumentLibrary Component
 *
 * Manages reusable documents that can be attached to permit applications:
 * - License copies
 * - Insurance certificates (COI)
 * - W-9 forms
 * - Business licenses
 * - Other commonly used documents
 *
 * Documents can be set to auto-attach to new permit applications.
 */

import { useState, useEffect } from 'react';
import {
  Timestamp,
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
  FileText,
  Download,
  ExternalLink,
  Loader2,
  FolderOpen,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Paperclip,
} from 'lucide-react';
import {
  ReusableDocument,
  ReusableDocumentType,
  REUSABLE_DOCUMENT_LABELS,
  isExpired,
  isExpiringSoon,
  daysUntilExpiration,
} from '@/types/contractor';

interface DocumentLibraryProps {
  contractorId: string;
  onSelectDocument?: (document: ReusableDocument) => void;
  selectionMode?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export function DocumentLibrary({
  contractorId,
  onSelectDocument,
  selectionMode = false,
}: DocumentLibraryProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [documents, setDocuments] = useState<ReusableDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ReusableDocument | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    documentType: '' as ReusableDocumentType,
    title: '',
    description: '',
    hasExpiration: false,
    expirationDate: '',
    autoAttachToNewJobs: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Fetch documents from Firestore
  useEffect(() => {
    if (!contractorId) return;

    const docsRef = collection(db, 'contractors', contractorId, 'reusable_documents');
    const q = query(docsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docData: ReusableDocument[] = [];
        snapshot.forEach((doc) => {
          docData.push({ id: doc.id, ...doc.data() } as ReusableDocument);
        });
        setDocuments(docData);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching documents:', error);
        toast({
          title: 'Error',
          description: 'Failed to load documents',
          variant: 'destructive',
        });
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contractorId, toast]);

  // Reset form
  const resetForm = () => {
    setFormData({
      documentType: '' as ReusableDocumentType,
      title: '',
      description: '',
      hasExpiration: false,
      expirationDate: '',
      autoAttachToNewJobs: false,
    });
    setSelectedFile(null);
    setSelectedDocument(null);
  };

  // Open dialog for adding new document
  const handleAddDocument = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  // Open dialog for editing document
  const handleEditDocument = (document: ReusableDocument) => {
    setSelectedDocument(document);
    setFormData({
      documentType: document.documentType,
      title: document.title,
      description: document.description || '',
      hasExpiration: document.hasExpiration,
      expirationDate: document.expirationDate || '',
      autoAttachToNewJobs: document.autoAttachToNewJobs,
    });
    setIsDialogOpen(true);
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF, image, or Word document',
          variant: 'destructive',
        });
        return;
      }
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 10MB',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);

      // Auto-fill title from filename if empty
      if (!formData.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setFormData((prev) => ({ ...prev, title: nameWithoutExt }));
      }
    }
  };

  // Save document
  const handleSaveDocument = async () => {
    // Validation
    if (!formData.documentType || !formData.title) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in document type and title',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedDocument && !selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to upload',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      let fileUrl = selectedDocument?.fileUrl;
      let fileName = selectedDocument?.fileName;
      let fileSize = selectedDocument?.fileSize;
      let fileType = selectedDocument?.fileType;

      // Upload file if selected
      if (selectedFile) {
        const timestamp = Date.now();
        const storagePath = `reusable_documents/${contractorId}/${timestamp}-${selectedFile.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, selectedFile);
        fileUrl = await getDownloadURL(storageRef);
        fileName = selectedFile.name;
        fileSize = selectedFile.size;
        fileType = selectedFile.type;
      }

      const documentData = {
        documentType: formData.documentType,
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        fileName,
        fileUrl,
        fileSize,
        fileType,
        hasExpiration: formData.hasExpiration,
        expirationDate: formData.hasExpiration ? formData.expirationDate : null,
        autoAttachToNewJobs: formData.autoAttachToNewJobs,
        contractorId,
        uploadedBy: user?.uid || null,
        updatedAt: Timestamp.now(),
      };

      if (selectedDocument) {
        // Update existing document
        const docRef = doc(
          db,
          'contractors',
          contractorId,
          'reusable_documents',
          selectedDocument.id
        );
        await setDoc(docRef, documentData, { merge: true });
        toast({
          title: 'Document updated',
          description: 'Your document has been updated successfully',
        });
      } else {
        // Create new document
        const newDocRef = doc(collection(db, 'contractors', contractorId, 'reusable_documents'));
        await setDoc(newDocRef, {
          ...documentData,
          id: newDocRef.id,
          createdAt: Timestamp.now(),
        });
        toast({
          title: 'Document added',
          description: 'Your document has been added to the library',
        });
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving document:', error);
      toast({
        title: 'Error',
        description: 'Failed to save document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async () => {
    if (!selectedDocument) return;

    setIsDeleting(true);

    try {
      // Delete from Firestore
      const docRef = doc(
        db,
        'contractors',
        contractorId,
        'reusable_documents',
        selectedDocument.id
      );
      await deleteDoc(docRef);

      // Try to delete from storage (may fail if URL format is different)
      if (selectedDocument.fileUrl) {
        try {
          const storageRef = ref(storage, selectedDocument.fileUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          // Storage deletion failed, but Firestore deletion succeeded
          console.warn('Failed to delete file from storage:', storageError);
        }
      }

      toast({
        title: 'Document deleted',
        description: 'The document has been removed from your library',
      });
      setIsDeleteDialogOpen(false);
      setSelectedDocument(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Get expiration status badge
  const getExpirationBadge = (document: ReusableDocument) => {
    if (!document.hasExpiration || !document.expirationDate) return null;

    if (isExpired(document.expirationDate)) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Expired
        </Badge>
      );
    }
    if (isExpiringSoon(document.expirationDate, 30)) {
      const days = daysUntilExpiration(document.expirationDate);
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-600 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {days} days
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-green-600 border-green-600 flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Valid
      </Badge>
    );
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Document Library</CardTitle>
              <CardDescription>
                Reusable documents for permit applications
              </CardDescription>
            </div>
            <Button onClick={handleAddDocument} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Document
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No documents in your library yet</p>
              <p className="text-sm mt-1">
                Add documents like license copies, insurance COIs, and W-9s for easy reuse
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{document.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {document.fileName} ({formatFileSize(document.fileSize)})
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {REUSABLE_DOCUMENT_LABELS[document.documentType] || document.documentType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getExpirationBadge(document)}
                        {document.autoAttachToNewJobs && (
                          <Badge variant="outline" className="text-xs">
                            <Paperclip className="h-3 w-3 mr-1" />
                            Auto-attach
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {selectionMode && onSelectDocument ? (
                        <Button
                          size="sm"
                          onClick={() => onSelectDocument(document)}
                        >
                          Select
                        </Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <a
                                href={document.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a
                                href={document.fileUrl}
                                download={document.fileName}
                                className="flex items-center"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditDocument(document)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                setSelectedDocument(document);
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Document Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedDocument ? 'Edit Document' : 'Add Document'}
            </DialogTitle>
            <DialogDescription>
              {selectedDocument
                ? 'Update document details'
                : 'Add a reusable document to your library'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="documentType">Document Type *</Label>
              <Select
                value={formData.documentType}
                onValueChange={(value) =>
                  setFormData({ ...formData, documentType: value as ReusableDocumentType })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(REUSABLE_DOCUMENT_LABELS).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., 2024 General Liability COI"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional notes about this document"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">
                {selectedDocument ? 'Replace File' : 'Upload File *'}
              </Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                onChange={handleFileChange}
              />
              {selectedFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
              {selectedDocument && !selectedFile && (
                <p className="text-sm text-muted-foreground">
                  Current: {selectedDocument.fileName}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <Label htmlFor="hasExpiration">Has Expiration Date</Label>
                <p className="text-sm text-muted-foreground">
                  Track when this document expires
                </p>
              </div>
              <Switch
                id="hasExpiration"
                checked={formData.hasExpiration}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, hasExpiration: checked })
                }
              />
            </div>

            {formData.hasExpiration && (
              <div className="space-y-2">
                <Label htmlFor="expirationDate">Expiration Date</Label>
                <Input
                  id="expirationDate"
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expirationDate: e.target.value })
                  }
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label htmlFor="autoAttach">Auto-Attach to New Jobs</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically include in new permit applications
                </p>
              </div>
              <Switch
                id="autoAttach"
                checked={formData.autoAttachToNewJobs}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, autoAttachToNewJobs: checked })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDocument} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {selectedFile && <Upload className="h-4 w-4 mr-2" />}
                  {selectedDocument ? 'Update' : 'Add'} Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{selectedDocument?.title}" from your library.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
